import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef } from 'react'

type Options = {
  isEditing: boolean
  isBusy?: boolean
  onRequestClose: () => Promise<void> | void
  canCloseOnEnter?: (event: KeyboardEvent<HTMLElement>) => boolean
  isInsideIgnoredElement?: (target: Node) => boolean
}

export const useRestaurantInlineEditor = ({
  isEditing,
  isBusy = false,
  onRequestClose,
  canCloseOnEnter,
  isInsideIgnoredElement,
}: Options) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isComposingRef = useRef(false)

  const requestClose = useCallback(async () => {
    if (isBusy) {
      return
    }

    await onRequestClose()
  }, [isBusy, onRequestClose])

  useEffect(() => {
    if (!isEditing) {
      return
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (isBusy) {
        return
      }

      if (!(event.target instanceof Node)) {
        return
      }

      if (containerRef.current?.contains(event.target)) {
        return
      }

      if (isInsideIgnoredElement?.(event.target)) {
        return
      }

      void requestClose()
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown)
  }, [isBusy, isEditing, isInsideIgnoredElement, requestClose])

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true
  }, [])

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false
  }, [])

  const handleEditorKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter') {
        return
      }

      if (event.nativeEvent.isComposing || isComposingRef.current) {
        return
      }

      if (canCloseOnEnter && !canCloseOnEnter(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      void requestClose()
    },
    [canCloseOnEnter, requestClose],
  )

  return {
    containerRef,
    handleCompositionStart,
    handleCompositionEnd,
    handleEditorKeyDown,
  }
}
